// ===========================================
// The owner title (roles slice 2)
// ===========================================
// What the school calls its owner tier, the ADMIN role ("Principal"; "Admin" until the owner names
// it on the Roles page). Every string that names who can do an owner-only thing reads it here, and
// so do the chips, so the school sees its own word. While loading, or if the read fails, it is the
// default: a label must never block a page.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import * as adminSettingsApi from '../api/adminSettings.api';

/** Mirrors the backend's `DEFAULT_OWNER_TITLE`. */
export const DEFAULT_OWNER_TITLE = 'Admin';
export const OWNER_TITLE_MAX_LENGTH = 40;

export const ownerTitleKeys = {
  all: ['owner-title'] as const,
};

/** The owner title, e.g. "Principal". Never undefined. */
export function useOwnerTitle(): string {
  const { data } = useQuery({
    queryKey: ownerTitleKeys.all,
    queryFn: adminSettingsApi.getOwnerTitle,
    staleTime: 5 * 60 * 1000,
  });
  return data?.ownerTitle || DEFAULT_OWNER_TITLE;
}

/** The stored title for the editor: `isDefault` says the school has not named it yet. */
export function useOwnerTitleSetting() {
  return useQuery({
    queryKey: ownerTitleKeys.all,
    queryFn: adminSettingsApi.getOwnerTitle,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateOwnerTitle() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (update: adminSettingsApi.OwnerTitleUpdate) => adminSettingsApi.updateOwnerTitle(update),
    onSuccess: (data, update) => {
      queryClient.setQueryData(ownerTitleKeys.all, data);
      enqueueSnackbar(update.ownerTitle === undefined ? 'Saved' : 'Title saved', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to save the title', { variant: 'error' });
    },
  });
}
